import { useCallback } from 'react';
import type {
  EventTags,
  OptimizelyDecideOption,
  OptimizelyUserContext,
} from '@optimizely/optimizely-sdk';
import { formatEventName } from 'lib/sdks/analytics/plugins/optimizely';

import { useOptimizelyState } from './optimizely.reducer';

export enum EventName {
  CLICK_CATEGORY_SEARCH = 'click-category-search',
  SEARCH_MODAL_QUERY_LENGTH = 'search_modal_query_length',
  CONTACT_CONVERSION = 'contact_conversion',
  EXAMPLE_A_A_OPTI_ONLY = 'example_a_a_opti_only',
  JOB_DESCRIPTION_LENGTH = 'job_description_length',
  LEAD_ENRICHMENT_FORM = 'lead_enrichment_form',
  JOBS_USER_SELECTED_CONTACT_PREFERENCE = 'jobs.user_selected_contact_preference',
  multistep_review_form_stage_0 = 'multistep_review_form_stage_0',
  multistep_review_form_stage_1 = 'multistep_review_form_stage_1',
  multistep_review_form_stage_2 = 'multistep_review_form_stage_2',
}

export enum Experiment {
  TEST = 'expirement-flag',
  PERSISTENT_A_A = 'persistent_a_a', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/persistent_a_a/
  SEMANTIC_SEARCH = 'semantic-search', //https://app.optimizely.com/v2/projects/30302000538/flags/manage/semantic-search/
  CLICK_TO_REVEAL_PHASE_2 = 'click-to-reveal-phase-2', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/click-to-reveal-phase-2/
  LOAN_BANNER_HOMEPAGE = 'loan_banner_homepage', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/loan_banner_homepage
  LOAN_BANNER_MESSAGE_SUCCESS = 'loan_banner_message_success', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/loan_banner_message_success
  LOAN_BANNER_CHAT = 'loan_banner_chat', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/loan_banner_chat
  LOAN_BANNER_BOOKED_JOBS = 'loan_banner_jobs', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/loan_banner_jobs
  SEARCH_ALGO_EXPERIMENT = 'search_algo_experiment', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/search_algo_experiment/
  RAQ_ENTRY_POINT = 'raq_entry_point', //https://app.optimizely.com/v2/projects/30302000538/flags/manage/raq_entry_point
  JOB_DESCRIPTION_CONVERSION_V2 = 'describe-your-job-conversion-v2',
  LEAD_ENRICHMENT_FORM = 'lead_enrichment_form',
  GUARANTEE_BANNER = 'guarantee-banner-inclusion-on-serp',
  APP_PROMOTION_POPUP_SEARCH = 'app-promotion-popup-search',
  CAT_PAY_VOUCHERS = 'cat-pay-vouchers', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/cat-pay-vouchers
  RAQ_JOB_DESCRIPTION = 'raq-job-description-experiment',
  MULTISTEP_REVIEW_FORM_ORDER = 'multistep_review_form_order', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/multistep_review_form_order
  RAQ_JOB_STARTDATE = 'raq-job-startdate-experiment',
  SEMANTIC_SEARCH_VARIANTS = 'md-780-semantic-search-variants', //https://app.optimizely.com/v2/projects/30302000538/flags/manage/md-780-semantic-search-variants/
  PREFERRED_CONTACT_METHOD = 'preferred-contact-method',
  SOCIAL_PROOFING_BADGE = 'social-proofing-badge',
  SEARCH_SORTING = 'search_page_sorting',
  CONSUMER_GUIDANCE_BANNER = 'consumer_guidance_banner', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/consumer_guidance_banner/rules/Staging
  SAVE_JOB_FEEDBACK_SURVEY = 'save-job-feedback-survey',
  MD_790_EXPRESS_SEARCH_RAQ_CARD = 'md-790-express-search-raq-card', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/md-790-express-search-raq-card
  EXPRESS_REDIRECT_SEARCH_MODAL = 'express_redirect_search_modal', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/express_redirect_search_modal
  ZERO_AUTH_PAYMENTS_FLAG = 'zero-auth-payments-flow-flag', // https://app.optimizely.com/v2/projects/30302000538/flags/manage/zero-auth-payments-flow-flag/
}

export const useOptimizely = () => {
  const { user, loading } = useOptimizelyState();

  const trackEventInOptimizely = useCallback(
    (eventName: EventName, eventTags?: EventTags) => {
      if (user?.trackEvent) {
        return user.trackEvent(formatEventName(eventName), eventTags);
      }
    },
    // User object is unstable, trackEvent function is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.trackEvent],
  );

  // try to algin to https://docs.developers.optimizely.com/feature-experimentation/docs/optimizelydecision-javascript#reasons
  const failSafeDecision = {
    variationKey: null,
    enabled: false,
    variables: {},
    ruleKey: null,
    flagKey: '',
    userContext: {} as OptimizelyUserContext,
    reasons: ['error occurred failsafe decision returned'],
  };

  const decide = useCallback(
    (key: Experiment, options?: OptimizelyDecideOption[]) => {
      if (user?.decide) {
        return user.decide(key, options);
      }

      return failSafeDecision;
    },
    // User object is unstable, decide function is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.decide],
  );

  const isVariantActive = useCallback(
    (experiment: Experiment, variantKey: string) => {
      if (loading) return false;

      const decision = decide(experiment);

      if (!decision?.enabled) {
        return false;
      }

      return decision?.variationKey === variantKey;
    },
    [loading, decide],
  );

  return { trackEventInOptimizely, decide, loading, isVariantActive };
};
